// B-55 — which cierre client the Cierres screens are talking about.
//
// The selector lives in the shell's header; the screens arrive as `props.children`, created by
// `App.js` and handed in. So there is no prop path from the one to the others, and injecting the
// code with `cloneElement` would leave each screen reading a value with no visible source. This is
// the app's only React context, and deliberately the smallest one that works: the chosen code, the
// list it came from, and whether that list has arrived.
//
// `listo` is the load-bearing part. Every screen's fetch waits on it, because a `filterOffices`
// fired before the list lands carries no `clientCode` — which is the 400 this context exists to
// prevent, and on a business with two clients it would be the first thing the operator sees.
//
// Nothing here names a client; see `utils/cierreCliente.js`.
//
// B-77 — it also holds the client's day in progress (`diaEnCurso`, `useDiaEnCurso.js`): the panels
// are remounted whenever a day is opened, so a run followed from inside one was lost on the first
// click. The same value chosen once and needed by every screen the shell frames — this context's
// reason to exist — so it rides here rather than in a second context.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { listCierreClients } from "../../../ApiCalls/mantisBusiness";
import { claveDeCliente, clienteInicial } from "../../../utils/cierreCliente";
import { correctionError } from "../../../utils/cierreCorrections";
import { getFromStorage, setInStorage } from "../../../utils/storage";
import { DIA_INACTIVO, useDiaEnCurso } from "./useDiaEnCurso";

const VACIO = { clientes: [], clientCode: "", elegirCliente: () => {}, listo: false, error: "", diaEnCurso: DIA_INACTIVO };

const ClienteCierresContext = createContext(VACIO);

export function ClienteCierresProvider({ token, mantisBusinessId, children }) {
  const [clientes, setClientes] = useState([]);
  const [clientCode, setClientCode] = useState("");
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!mantisBusinessId) return undefined;
    let activo = true;
    setListo(false);
    (async () => {
      const response = await listCierreClients(token, mantisBusinessId).catch(() => null);
      if (!activo) return;
      // A failed list still releases the screens, with no code. For a business that owns one
      // client that is exactly what the panel did before this task — the backend resolves the
      // only one — and for a business that owns several the backend's own 400 is a better thing
      // to show than a panel that never stops loading.
      if (!response?.success) {
        setError(correctionError(response));
        setClientes([]);
        setClientCode("");
        setListo(true);
        return;
      }
      const lista = response.data.clients || [];
      setError("");
      setClientes(lista);
      setClientCode(clienteInicial(lista, getFromStorage(claveDeCliente(mantisBusinessId))));
      setListo(true);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId]);

  const elegirCliente = useCallback(
    (code) => {
      setClientCode(code);
      const clave = claveDeCliente(mantisBusinessId);
      if (clave) setInStorage(clave, code);
    },
    [mantisBusinessId]
  );

  const timezone = clientes.find((c) => c.code === clientCode)?.timezone;
  const diaEnCurso = useDiaEnCurso({ token, mantisBusinessId, clientCode: listo ? clientCode : "", timezone });

  const valor = useMemo(
    () => ({ clientes, clientCode, elegirCliente, listo, error, diaEnCurso }),
    [clientes, clientCode, elegirCliente, listo, error, diaEnCurso]
  );

  return <ClienteCierresContext.Provider value={valor}>{children}</ClienteCierresContext.Provider>;
}

export function useClienteCierres() {
  return useContext(ClienteCierresContext);
}
